import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    AlertServiceV2,
    AlertTypeRegistry,
    LatencyComponentService,
    PickableComponent,
    ValidationErrorIndex,
    alertTypeLabel,
} from '@netfoundry-ui/shared/apiv2';
import { GrowlerData, GrowlerService } from '@netfoundry-ui/shared/growler';
import {
    AlertTypeView,
    AutoThresholds,
    ComponentType,
    LatencyPolicy,
    LatencyPolicyKind,
    LatencyPolicyRequest,
    LatencyPolicyUpdate,
} from '@netfoundry-ui/shared/model';
import { ApiService, LoggerService, URLS } from '@netfoundry-ui/shared/services';
import { isEmpty, isNil } from 'lodash';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import {
    LadderPlaceholders,
    PolicyKindOption,
    RATIO_ONLY_NOTE,
    alertTypeWatches,
    acceptsFixedThresholds,
    componentPartLabels,
    componentTypeLabel,
    keyedBySentence,
    kindsFor,
    ladderPlaceholders,
    multiplierBasis,
    policyKind,
    validateScope,
} from './latency-policy-text';

/** The route segment that means "a policy that does not exist yet". */
const CREATE_SEGMENT = 'create';

/**
 * Shortest query sent to the controller.
 *
 * A one-character query matches most of a network, so it costs a round trip to answer with a
 * list nobody can use. The picker holds until there are two.
 */
const SEARCH_MIN_LENGTH = 2;

/**
 * How long the picker waits before asking.
 *
 * Long enough that a request does not go out per keystroke, short enough that the list has
 * moved by the time the reader looks up from the keyboard.
 */
const SEARCH_DELAY_MS = 300;

/**
 * A Ziti id, in the placeholder of a box that has no list to search.
 *
 * **An example rather than the word "id".** A policy is keyed by the component's Ziti id,
 * which is not the NetFoundry UUID beside it in every other listing — and the shape is what
 * says which of the two is wanted, where naming it would not.
 *
 * Not a real component. It is the length and alphabet the controller issues, and nothing
 * else: an id copied from a deployment would be a live component named in the source, and a
 * reader who pasted it would key a policy at somebody else's router.
 */
const EXAMPLE_COMPONENT_ID = '7ML1E8N5Sx438PQUOBAR9f';

/**
 * The JSON paths this form draws a box for, so it can tell them from the ones it does not.
 *
 * The service names a fault by the path of the field it is about — `$.auto.floorMillis`,
 * `$.alertType`. Everything on this list is attached to an input. Everything else the
 * service names goes to the form's own error block, which is what stops a rule shipped
 * after this console was written from becoming an invisible refusal.
 *
 * `$.auto` and `$.fixed` are the xor rule — a policy is one or the other, never both and
 * never neither — so they belong to the kind picker rather than to any number.
 *
 * `componentIds` is not here: how many boxes there are comes from the alert type, so the
 * paths are built per index. See {@link LatencyPolicyFormComponent.claimedPaths}.
 */
const FIELD_PATHS = [
    '$.alertType',
    /* The pair, not either box. A rule the two ids break together — the same edge router
     * chosen twice — is a fault about the collection, and the service names it on the bare
     * path. Claimed so it lands under the boxes it is about: unclaimed it would fall through
     * to the block under the thresholds, which is a fault about the scope reported a panel
     * away from the controls that fix it. `$.auto`/`$.fixed` are claimed for the same reason. */
    '$.componentIds',
    '$.auto',
    '$.fixed',
    '$.auto.minorMultiplier',
    '$.auto.majorMultiplier',
    '$.auto.criticalMultiplier',
    '$.auto.floorMillis',
    '$.auto.ceilingMillis',
    '$.fixed.minorMillis',
    '$.fixed.majorMillis',
    '$.fixed.criticalMillis',
];

/**
 * The boxes on the threshold line, by JSON path and the column each sits under.
 *
 * **The column name, not the service's field name.** A ladder's faults are listed under the
 * whole line rather than under the box they are about — a number column is too narrow to
 * hold a sentence, and one wrapped under a box would push its neighbours out of line. So the
 * sentence has to say which column it is about, and the name that helps is the one written
 * over that column: `Major`, not `majorMultiplier`.
 *
 * Keyed by kind because only one line is ever on screen, and the two measure the same
 * columns in different things.
 */
const LADDER_FIELDS: Record<LatencyPolicyKind, { path: string; label: string }[]> = {
    AUTO: [
        { path: '$.auto.floorMillis', label: 'Floor' },
        { path: '$.auto.minorMultiplier', label: 'Minor' },
        { path: '$.auto.majorMultiplier', label: 'Major' },
        { path: '$.auto.criticalMultiplier', label: 'Critical' },
        { path: '$.auto.ceilingMillis', label: 'Ceiling' },
    ],
    FIXED: [
        { path: '$.fixed.minorMillis', label: 'Minor' },
        { path: '$.fixed.majorMillis', label: 'Major' },
        { path: '$.fixed.criticalMillis', label: 'Critical' },
    ],
};

/**
 * What the picker's panel is layered from.
 *
 * PrimeNG adds its own 1000 for an overlay, so the panel lands just over the `99999` this
 * console gives its other dropdown panels and well under the `99999999` it gives dialogs.
 */
const PICKER_BASE_Z_INDEX = 99999;

/**
 * Create a latency policy, or move an existing one's thresholds.
 *
 * A page rather than a dialog, and drawn with the same header, sections and fields every
 * other create page in this console uses. Setting a threshold is not a quick confirmation
 * — it is a ladder of numbers with rules binding them, and it wants the room.
 *
 * <h2>Four things the contract forces on this form</h2>
 *
 *  - **A policy is auto or fixed, never both and never neither.** Auto grades against the
 *    subject's own learned baseline; fixed grades against absolute milliseconds. The form
 *    shows one field set at a time for that reason: two sets on screen would invite a save
 *    the service answers 400 to, and a policy carrying neither grades nothing at all.
 *
 *  - **The two are measured in different things.** An auto ladder is `3×`, `6×`, `12×`; a
 *    fixed one is `100 ms`, `250 ms`, `1000 ms`. Every field carries its unit, because a
 *    form that showed them alike would invite one to be typed as the other.
 *
 *  - **Everything created here is scoped to one component.** The network policies are
 *    edited in place in the section above the listing, so this page has no scope choice to
 *    make: the component pair is required, and it always goes out whole. Half a pair is a
 *    400, because it keys a policy nothing can ever look up.
 *
 *  - **The scope is the key, so it cannot be edited.** A policy's id is derived from its
 *    network, its alert type and its component. Pointing a policy at something else is a
 *    delete and a create, so on an existing policy this page shows the scope and lets
 *    nobody touch it rather than offering a field the save would drop — and that includes
 *    a network policy somebody reached by its id, which is why the locked line still names
 *    one. **The kind is not part of the key**, though, so switching auto to fixed edits the
 *    policy in place.
 *
 *  - **A key already covered is a 409.** Creating and updating are separate privileges, so
 *    the service refuses rather than silently overwriting a threshold the caller did not
 *    know about. That answer is reported here as what it is — the policy already exists,
 *    edit it — rather than as a failure.
 *
 * <h2>What the page never claims</h2>
 *
 * Deleting a policy restores whatever sits under it: an override falls back to the network
 * default, and the network default falls back to the shipped defaults. **Latency alerting
 * cannot be switched off from here.** Nothing on this page suggests it can, because a
 * reader who set a multiplier of 500 to silence something would find it still paging.
 */
@Component({
    selector: 'app-latency-policy-form',
    templateUrl: './latency-policy-form.component.html',
    styleUrls: ['./latency-policy-form.component.scss'],
    standalone: false,
})
export class LatencyPolicyFormComponent implements OnInit, OnDestroy {
    readonly ratioOnlyNote = RATIO_ONLY_NOTE;
    readonly componentTypeLabel = componentTypeLabel;
    readonly alertTypeLabel = alertTypeLabel;
    /* What each alert type is keyed by, for the card that offers it. */
    readonly keyedBySentence = keyedBySentence;

    /** The policy being edited, or undefined while this is a create. */
    policy?: LatencyPolicy;

    /** The id from the route: a uuid, or `create`. */
    entityId = CREATE_SEGMENT;

    alertType = '';

    /**
     * The components this policy is keyed by, one entry per box on screen.
     *
     * **Sized by the alert type, never by this form.** `componentIdParts` says a link policy
     * is keyed by the two edge routers it joins and a service policy by one service id, so
     * the array is re-sized whenever the alert type changes — and emptied with it, because a
     * router id is not a service id and carrying one across would key a policy at something
     * that does not exist.
     */
    componentIds: string[] = [];

    /**
     * Which way this policy grades.
     *
     * Auto to start, because it is the one that adapts per subject. Editable on an existing
     * policy as well as a new one: the kind is part of the thresholds rather than part of
     * the key, so switching it edits the policy in place instead of making a second one.
     */
    kind: LatencyPolicyKind = 'AUTO';

    /* Held as the numbers a person types, in the units the service holds them in. Both sets
     * are kept while only one is shown, so switching the kind back and forth does not throw
     * away what was typed under the other. Only the selected kind is ever sent. */
    minorMultiplier?: number;
    majorMultiplier?: number;
    criticalMultiplier?: number;
    floorMillis?: number;
    /**
     * The network policy for the selected alert type, when one is set and could be read.
     *
     * Reference numbers only — see {@link loadNetworkPolicy}. Cleared with the alert type,
     * because a ladder from the type the reader left is the one number here that would
     * mislead: it would grey a step ladder into a drift row's boxes.
     */
    private networkPolicy?: LatencyPolicy;
    ceilingMillis?: number;

    minorMillis?: number;
    majorMillis?: number;
    criticalMillis?: number;

    isLoading = false;
    processing = false;

    /**
     * Every reason this page refuses to send the form, or an empty list.
     *
     * **The component key, and nothing else.** The service validates the ladder and names
     * the field each fault is about, so a second copy of those rules here would only be a
     * save blocked by a wording that had drifted from the one that stopped it — and a
     * client rule stricter than the service's is a save nobody can complete. A bad ladder
     * therefore goes out and comes back a 400, which {@link handleSaveError} turns into
     * messages under the boxes.
     *
     * The key is the one rule the service cannot apply for this page. A create with no
     * component ids is not refused there — it is a network-wide policy, which is a real
     * thing to write and not what this page writes. See {@link ownScopeErrors}.
     */
    get errors(): string[] {
        return this.ownScopeErrors;
    }

    /**
     * Whether a save has been attempted.
     *
     * What opens every box up to the service's answer, not only the ones somebody has left.
     * Until then a form fills in quietly: turning red while somebody is on the second of
     * five boxes would be reporting that they have not finished typing.
     */
    showErrors = false;

    /** Why the save failed at the service, or ''. Kept apart from {@link errors}: those
     * are this page's rules, this is the service's answer. */
    saveError = '';

    /**
     * What the service says is wrong, field by field.
     *
     * From the pre-submit check — the same request with the `nf-validate` header on it,
     * which creates nothing and answers with the fields that would be refused. **This owns
     * the message under a box.** {@link errors} is this page's own gate on the save button
     * and stays a summary; a rule written twice, once here and once in the service, is a
     * rule that will be worded two ways by the second release.
     *
     * Empty until a field has been left, and re-read every time one is. See
     * {@link checkWithService}.
     */
    serverErrors = new ValidationErrorIndex();

    currentNetwork: any = {};

    private subscription = new Subscription();

    /** The query each box is waiting on, so a slow reply cannot overwrite a faster one. */
    private pendingQueries: string[] = [];

    /**
     * The box that was just left, and nothing else.
     *
     * **What separates a fault from an unfinished form.** One check answers about the whole
     * body, so leaving the first box returns the faults of every box nobody has reached yet.
     * Showing those would be reporting that somebody has not finished typing.
     *
     * **Replaced on every blur, never added to.** A reader working down the form is told
     * about the box they just left, not about that one and every earlier one — a column of
     * red that grows as somebody fills a form in reads as a form getting worse while it is
     * getting better. The fault does not go away when the message does: the box is still
     * wrong, {@link errors} still knows, and pressing Create shows every one of them at
     * once. That is what {@link showErrors} is for.
     *
     * Usually one path. The kind picker replaces it with two, because the rule it can break
     * is about the pair.
     */
    private lastBlurred = new Set<string>();

    /** The body the last check went out with, so an unchanged form is not asked about twice. */
    private lastCheckedBody = '';

    /**
     * Which check is current.
     *
     * A slow answer landing after a faster one would paint the form with faults about a
     * body that has since changed — the same race the component pickers guard with
     * {@link pendingQueries}, and the same guard.
     */
    private checkSequence = 0;

    /** True while {@link saveError} holds a 403 from a check, so a later accepted check can clear it. */
    private checkDenied = false;

    constructor(
        private alertService: AlertServiceV2,
        private alertTypes: AlertTypeRegistry,
        private components: LatencyComponentService,
        private apiService: ApiService,
        private growlerService: GrowlerService,
        private logger: LoggerService,
        private router: Router,
        private route: ActivatedRoute
    ) {}

    /** Whether this page is moving an existing policy rather than making one. */
    get isEditing(): boolean {
        return this.entityId !== CREATE_SEGMENT;
    }

    get title(): string {
        return this.isEditing ? 'Edit Latency Policy' : 'Create Latency Policy';
    }

    get hasNetwork(): boolean {
        return !isEmpty(this.currentNetwork.id);
    }

    /**
     * The alert types this form will write a policy for.
     *
     * **Only the ones the service says a policy decides.** A policy written against a type
     * whose `latencyPolicy` is false is stored, served back, and never read by any detector
     * — so offering one here would be offering a setting that silently does nothing. The
     * list comes from `/alert-types` rather than from a table in this console, because a
     * table drifts the moment a detector ships and drifts towards hiding the new type.
     */
    get alertTypeOptions(): AlertTypeView[] {
        return this.alertTypes.latencyPolicyTypes;
    }

    /**
     * What the service says about the selected alert type, or undefined before one is
     * chosen and for a type the registry does not hold.
     *
     * One lookup rather than four. The ids a policy is keyed by, the kinds it may grade by,
     * whether it takes a ceiling and what its multipliers are of all come from here, so
     * they cannot end up answering about different types.
     */
    get selectedType(): AlertTypeView | undefined {
        return this.alertTypeOptions.find((option) => option.alertType === this.alertType);
    }

    /**
     * The kinds of id the selected alert type is keyed by, in order.
     *
     * Straight from the service. A console that decided this for itself would be one
     * detector away from sending the wrong number of ids, which is a 400.
     */
    get componentIdParts(): ComponentType[] {
        return this.selectedType?.componentIdParts || [];
    }

    /**
     * The kind cards to draw, with the wording the selected alert type calls for.
     *
     * A type whose condition is a ratio between two windows offers auto alone — see
     * {@link kindsFor}. Nothing is offered here and then refused with a 400.
     */
    get policyKinds(): PolicyKindOption[] {
        return kindsFor(this.selectedType);
    }

    /**
     * Whether the selected alert type grades a ratio and nothing else.
     *
     * **The ceiling goes with the fixed form.** Both are absolute milliseconds, and the
     * service refuses both on such a type: an absolute number may suppress but may not
     * fire. The floor only suppresses, so it stays.
     */
    get isRatioOnly(): boolean {
        return !acceptsFixedThresholds(this.selectedType);
    }

    /** What the multipliers are multiples of, for the rule under the boxes. */
    get multiplierBasis(): string {
        return multiplierBasis(this.selectedType?.comparisonWindow);
    }

    /** What the chosen alert type watches for, or '' before one is chosen. */
    get alertTypeWatches(): string {
        return alertTypeWatches(this.selectedType);
    }

    /**
     * The ladder this policy would inherit if it did not exist: the network policy for the
     * selected alert type, and the platform ladder under that.
     *
     * The same order the service resolves in — an override sits over the network policy, and
     * the network policy over the platform's — so the greyed-out numbers in the boxes are
     * what would actually apply if the boxes were left alone, not just what the platform
     * ships.
     */
    get inheritedLadder(): AutoThresholds | undefined | null {
        return this.networkPolicy?.auto || this.selectedType?.fleetDefaults;
    }

    /**
     * What each empty box in the auto ladder shows, from whatever the ladder would inherit.
     *
     * A placeholder rather than a value — see {@link ladderPlaceholders}. Nothing here is
     * ever sent: the boxes are required, so an empty one is a save that fails rather than a
     * save that quietly repeats the fallback.
     *
     * Empty throughout before a type is chosen, and for a type publishing no defaults. A
     * number left over from another alert type would be the one mistake here that matters,
     * because a drift ladder and a step ladder differ by a factor of three.
     */
    get placeholders(): LadderPlaceholders {
        return ladderPlaceholders(this.inheritedLadder);
    }

    /**
     * The options for each id box, in the order the boxes appear.
     *
     * Loaded per kind — see {@link loadComponentOptions} — and empty for a kind that has no
     * listing, which is what puts that box back on free text. A link policy's two boxes are
     * both edge routers and share one list rather than loading it twice.
     */
    componentOptions: PickableComponent[][] = [];

    /**
     * What each box's panel is offering right now.
     *
     * The answer to the last query typed into that box, which is a different thing from
     * {@link componentOptions}: those are the whole listing, read once and used to decide
     * whether the box can offer a list at all, and these are what the controller said about
     * one search. Per box rather than per kind, because a link policy's two router boxes are
     * searched independently.
     */
    componentSuggestions: PickableComponent[][] = [];

    /**
     * What each box's control holds — the option somebody picked, or the text they typed.
     *
     * The control binds the option itself rather than its id, so the box can show the name
     * while the policy is keyed by the id. A box with no listing binds the string instead;
     * see {@link takeComponentPick}, which reads both.
     */
    componentPicks: (PickableComponent | string | null)[] = [];

    readonly searchMinLength = SEARCH_MIN_LENGTH;
    readonly searchDelay = SEARCH_DELAY_MS;

    /**
     * Where the picker's panel sits in the stack.
     *
     * PrimeNG gives an overlay `baseZIndex + 1000`, and left at its default of 0 that is far
     * under this console's own chrome: `global.scss` in `ziti-console-lib` runs the Angular
     * Material overlay container at `99999999 !important` and its own older PrimeNG panels at
     * `99999` and `99999999`. A panel at 1000 is drawn, holds the right options, and is
     * painted over — which looks exactly like a picker that answers nothing.
     *
     * Above the console's page furniture and below its dialogs, because a dialog opened over
     * this form should cover the form's own dropdown rather than appear behind it.
     *
     * A constant object rather than an inline literal: a new object each change detection
     * cycle is a changed input, and this one feeds the overlay.
     */
    readonly pickerOverlay = { baseZIndex: PICKER_BASE_Z_INDEX };

    /** What to label each id box with. A repeated kind is lettered — see {@link componentPartLabels}. */
    get componentIdLabels(): string[] {
        return componentPartLabels(this.componentIdParts);
    }

    /**
     * Whether the thresholds may be typed into.
     *
     * **The thresholds open when the policy knows what it applies to.** Every number below
     * is measured in something the selected alert type decides — which kinds are offered,
     * whether there is a ceiling at all, what a multiplier is a multiple of, and what the
     * empty boxes show as inherited. A ladder typed before a type is chosen is a ladder
     * typed against a guess, and choosing a type then takes a card and a box away, possibly
     * one that already has a number in it.
     *
     * **It is also what the service can be asked about.** A create is checked only once it
     * carries its whole key — see {@link checkWithService} — so a box left open before then
     * is one whose faults nothing would report. Shut, it cannot be focused, so no blur can
     * ask a question that goes nowhere.
     *
     * Three ways in, and the third is the one worth naming: see {@link hasNoComponentKey}.
     * An edit is always open, because the scope of a stored policy is its key and `PATCH`
     * carries the thresholds alone.
     */
    get canEditThresholds(): boolean {
        return this.isEditing || this.hasWholeKey || this.hasNoComponentKey;
    }

    /**
     * Why the thresholds are shut, or `''` while they are open.
     *
     * Two states, and they are different questions. Without a type the reader has not said
     * what the numbers are measured in; with one, they have not said what the numbers apply
     * to. **The second borrows the sentence the save is refused with** — see
     * {@link ownScopeErrors} — so the shut panel and the refused Create are worded once and
     * cannot drift apart by a release.
     */
    get thresholdsLockedReason(): string {
        if (this.canEditThresholds) {
            return '';
        }
        if (isEmpty((this.alertType || '').trim())) {
            return 'Choose an alert type first. What the thresholds are measured in depends on it.';
        }
        return this.ownScopeErrors[0] || '';
    }

    /** Whether the auto field set is the one on screen. */
    get isAuto(): boolean {
        return this.kind === 'AUTO';
    }

    /**
     * What the form would send, as the service takes it.
     *
     * The component ids always go out, because everything created here is scoped to one
     * component — the network policies are edited in the section over the listing. How many
     * there are comes from the alert type, not from here.
     */
    get request(): LatencyPolicyRequest {
        return {
            alertType: (this.alertType || '').trim(),
            /* Blanks dropped rather than sent: the service counts the ids, and an empty
             * string is a part that is not there. Order is not sorted here — the service
             * sorts, and a second implementation of its key could only ever disagree. */
            componentIds: this.componentIds.map((id) => (id || '').trim()).filter((id) => !isEmpty(id)),
            ...this.thresholds,
        };
    }

    /**
     * The selected kind's numbers, and only that kind's.
     *
     * Exactly one key, because that is what the service takes: neither is a 400 and both is
     * a 400. The other set stays on the component so a reader who switches back finds their
     * work, and never reaches the wire.
     *
     * `ceilingMillis` is left off when it is blank rather than sent as null — it is
     * optional, and omitting it is what "no ceiling" means.
     */
    get thresholds(): LatencyPolicyUpdate {
        if (this.isAuto) {
            const auto: AutoThresholds = {
                minorMultiplier: this.minorMultiplier,
                majorMultiplier: this.majorMultiplier,
                criticalMultiplier: this.criticalMultiplier,
                floorMillis: this.floorMillis,
            };
            /* Guarded rather than cleared. The service refuses a ceiling on a ratio-only
             * type, and this is the single place `auto` is built — so one condition here is
             * enough, and a reader who switches back to a type that takes one finds the
             * number they typed. */
            if (!isNil(this.ceilingMillis) && !this.isRatioOnly) {
                auto.ceilingMillis = this.ceilingMillis;
            }
            return { auto };
        }
        return {
            fixed: {
                minorMillis: this.minorMillis,
                majorMillis: this.majorMillis,
                criticalMillis: this.criticalMillis,
            },
        };
    }

    /**
     * What the scope reads as on an existing policy, where it cannot be changed.
     *
     * Named rather than blank for a network default: an empty scope reads as "every
     * component" and as "no component", and only the first is right.
     */
    get lockedScope(): string {
        return this.lockedScopeParts.join(', ');
    }

    /**
     * The stored scope, one line per component it is keyed by.
     *
     * An array rather than a joined string, because the template renders one line each:
     * joined, HTML would collapse the spacing between two router ids into one space and the
     * pair would read as a single value.
     *
     * On the parts, never on the component type: the type is read-only and not always
     * served, so reading its absence as "no component" would report an override as a network
     * policy — opposite answers about how far a threshold reaches.
     */
    get lockedScopeParts(): string[] {
        if (!this.policy || isEmpty(this.storedParts)) {
            return ['Network policy — every component of this alert type'];
        }
        const labels = componentPartLabels(this.componentIdParts);
        /* Each part named by the box it came from, rather than the stored key. A link's key
         * is its two router ids joined with a `|`, and showing that would ask a reader to
         * parse a delimiter to find out which routers a threshold covers. */
        return this.storedParts.map((id, index) => {
            const label = labels[index] || componentTypeLabel(this.policy?.componentType);
            return isEmpty(label) ? id : `${label} · ${id}`;
        });
    }

    /**
     * The ids a stored policy is keyed by.
     *
     * `componentIds` when the service serves it. **Splitting `componentId` is the fallback,
     * not the reading**: the stored key is opaque and the parts are served precisely so a
     * form need not parse it — but a policy written before the field existed has only the
     * key, and a form that showed nothing for one would be worse than one that splits.
     */
    private get storedParts(): string[] {
        const served = this.policy?.componentIds || [];
        if (served.length > 0) {
            return served;
        }
        const key = this.policy?.componentId || '';
        return isEmpty(key) ? [] : key.split('|').filter((part) => !isEmpty(part));
    }

    ngOnInit(): void {
        this.subscription.add(
            /* By id, because `currentNetwork` is a `BehaviorSubject` that re-emits: the same
             * network arriving twice would read the listings again and, worse, hand the
             * pickers a fresh set of panels in the middle of somebody's search — which is
             * enough to make the answer to it never appear. */
            this.apiService.currentNetwork
                .pipe(distinctUntilChanged((before, after) => before?.id === after?.id))
                .subscribe((network) => {
                    this.currentNetwork = network || {};
                    /* What the alert type picker is built from. Loaded once per session and
                     * shared with the listing, so the two never offer different types. */
                    this.alertTypes.load(this.currentNetwork.id).then(() => this.loadComponentOptions());
                })
        );
        this.subscription.add(
            this.route.params.subscribe((params) => {
                const id = params['id'];
                this.entityId = isEmpty(id) ? CREATE_SEGMENT : id;
                if (this.isEditing) {
                    this.loadPolicy(this.entityId);
                }
            })
        );
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * Picks the alert type, and re-shapes the id boxes under it.
     *
     * **The boxes are emptied, not carried across.** A link policy is keyed by two edge
     * router ids and a service policy by one service id, so a value typed under one alert
     * type is meaningless under another — carrying it would key a policy at something that
     * does not exist, and the service would store it happily because an id is just a string.
     *
     * The count comes from `componentIdParts` on the type the reader just chose. Nothing
     * here decides it: a detector can ship a rule keyed differently, and the wrong count is
     * a 400.
     */
    setAlertType(alertType: string): void {
        this.alertType = alertType;
        /* The kind is re-shaped with the boxes. A type that grades a ratio and nothing else
         * offers no fixed card, so a form left on Fixed by the previous type would show an
         * auto ladder under a kind nothing can select back. Auto is the only kind such a
         * type has, and it is the default for every other one. */
        if (this.isRatioOnly) {
            this.kind = 'AUTO';
        }
        this.componentIds = this.componentIdParts.map(() => '');
        /* The controls are cleared with the ids they carry. A box left holding an option would
         * show a router name over an empty service id — the form claiming a scope it would not
         * send. */
        this.componentPicks = this.componentIdParts.map(() => null);
        /* The one place a panel is emptied. The boxes under a new alert type are new boxes —
         * a service box offering routers would be offering a scope no detector could match.
         * Everywhere else the panels are left alone, because replacing one mid-search is what
         * makes an answer arrive to nothing. */
        this.componentSuggestions = this.componentIdParts.map(() => []);
        this.pendingQueries = this.componentIdParts.map(() => '');
        /* The boxes under a new alert type are new boxes, so nobody has left one yet. A path
         * left behind would put the old type's fault under a box that is now asking for a
         * different kind of id. */
        this.lastBlurred.clear();
        this.loadComponentOptions();
        /* The reference ladder is per alert type, so it is re-read with the boxes. */
        this.loadNetworkPolicy(alertType);
        /* **Nothing is asked of the service here.** The boxes this type just shaped are
         * empty, so a check would answer with the faults of every one of them, and the type
         * itself is the one field that cannot be wrong — it came from the list the service
         * served. The first check goes out with the first component id: see
         * {@link setComponentId}. */
    }

    /**
     * Whether one box has a list behind it, or is a box to type an id into.
     *
     * **What decides whether the box refuses what somebody typed.** A box with a list takes
     * only what is in that list — a name that matches nothing is a typo, and letting it
     * through would key a policy at a component that does not exist.
     *
     * A kind with no listing — a link, a detector — takes the id as text instead, and so does
     * a listing that could not be read: a reader without permission to list routers still has
     * to be able to set a threshold, and a box that refused every id would be the one thing
     * worse than a box with no suggestions.
     *
     * False until the listing lands, so a box takes a typed id for as long as the read is in
     * flight and starts holding out for a name once it has one.
     */
    canPick(index: number): boolean {
        return (this.componentOptions[index] || []).length > 0;
    }

    /**
     * Answers one box's query, from the controller.
     *
     * **Asked rather than filtered.** The controller decides which names match, so a network
     * with more components than one page holds still offers the one being searched for — see
     * {@link LatencyComponentService.search}. An empty query, which is what the box's dropdown
     * button sends, is the cached full listing.
     *
     * The control gates the rate: `minLength` holds a query under {@link SEARCH_MIN_LENGTH}
     * characters and `delay` waits {@link SEARCH_DELAY_MS} for typing to stop, so a request
     * does not go out per keystroke.
     *
     * Two answers are dropped rather than shown. One for a query that is no longer what is in
     * the box — a slow reply landing after a faster one would offer matches for a word the
     * reader has already changed. One for an alert type that has changed while the request was
     * in flight, which would offer routers under a service box.
     *
     * **An answer that is kept always lands as a new array.** PrimeNG shows the panel when the
     * `suggestions` reference changes after it asked, so every answer has to be a change and
     * nothing else may be one.
     */
    searchComponents(index: number, query: string): void {
        const parts = this.componentIdParts;
        const kind = parts[index];
        if (isNil(kind)) {
            return;
        }
        const term = query || '';
        this.pendingQueries[index] = term;
        this.components.search(this.currentNetwork.id, kind, term).then((options) => {
            if (this.pendingQueries[index] !== term || this.componentIdParts.join() !== parts.join()) {
                return;
            }
            /* **Copied, so the reference always changes.** PrimeNG opens the panel on the
             * `suggestions` reference changing and on nothing else, and an empty query is
             * answered from the service's cache — the very array this box is already holding.
             * Assigned as it comes, that answer is invisible: same object, no change, no
             * panel. This is the one thing the chevron depends on, because the chevron always
             * asks with an empty query. */
            this.componentSuggestions[index] = [...options];
        });
    }

    /**
     * Takes whatever one box now holds, as the id a policy is keyed by.
     *
     * An option when somebody picked one from the list, a string when they typed into a box
     * that has no list to pick from, and null when they cleared it. The **Ziti** id is what
     * comes out of all three — see {@link LatencyComponentService}.
     */
    takeComponentPick(index: number, value: PickableComponent | string | null): void {
        this.componentPicks[index] = value;
        if (isNil(value)) {
            this.setComponentId(index, '');
            return;
        }
        this.setComponentId(index, typeof value === 'string' ? value.trim() : value.id);
    }

    /**
     * What one box says when it is empty.
     *
     * A box with a list to search says so. A box without one is asking for an id to be typed,
     * and an example of one says which id far better than the word "id" does — see
     * {@link EXAMPLE_COMPONENT_ID}.
     *
     * **`e.g.` is what makes it read as an example.** A bare id in a box the reader may type
     * into is indistinguishable from a value already in it, and this box is the one whose
     * value nothing validates against a list: a reader who took it for a filled-in field
     * would press Create and key a policy at nothing.
     */
    componentPlaceholder(index: number, label: string): string {
        return this.canPick(index) ? `Search ${label.toLowerCase()}` : `e.g. ${EXAMPLE_COMPONENT_ID}`;
    }

    /**
     * Takes the id the select bound.
     *
     * The option's value is the **Ziti** id, which is what a policy is keyed by — see
     * {@link LatencyComponentService}. The empty option clears the box.
     */
    setComponentId(index: number, componentId: string): void {
        this.componentIds[index] = componentId || '';
        /* **The first thing on this form worth asking the service about.** A check answers
         * about the whole body, and the scope is the first part of one that is meant — it is
         * also what reports a policy that already covers this component.
         *
         * The box becomes the one answered for, rather than waiting for `onBlur`. Clicking
         * an option in the panel does not always take the focus off the input, so a picked
         * id would otherwise be checked and the answer held back. Only a box that holds an
         * id: clearing one is not somebody saying they are finished with it. */
        if (!isEmpty(this.componentIds[index])) {
            /* The box and the pair, because a duplicate is a fault neither box carries on
             * its own: the second router is wrong only against the first. The kind picker
             * passes `$.auto` and `$.fixed` together for the same reason — see
             * {@link setKind}. Without the collection path here the service's answer would
             * arrive and be held back until somebody pressed Create. */
            this.answerFor(this.componentPath(index), '$.componentIds');
        }
        this.checkWithService();
    }

    /**
     * Switches between grading on a baseline and grading on absolute milliseconds.
     *
     * **Keeps both sets of numbers.** They are measured in different things, so neither can
     * be carried across, and throwing the hidden one away would lose a ladder somebody
     * typed because they clicked the wrong radio. Only the selected kind is ever sent.
     */
    setKind(kind: LatencyPolicyKind): void {
        /* A kind the selected type does not offer is refused here as well as absent from the
         * picker, because both answers come from one list. */
        if (this.kind === kind || !this.policyKinds.some((entry) => entry.value === kind)) {
            return;
        }
        this.kind = kind;
        /* Both, because the rule the picker can break is about the pair: exactly one of
         * them has to be sent, so neither path is the one that is wrong on its own. */
        this.fieldBlurred('$.auto', '$.fixed');
    }

    /**
     * One box has been left. Mark it answerable and ask the service about the form.
     *
     * **On blur rather than on every keystroke.** A threshold is a ladder — a major typed
     * before its minor is momentarily below it — so a check per character would tell
     * somebody they were wrong in the middle of being right. Leaving the box is the first
     * moment the value is meant.
     *
     * The whole body goes out, not the one field: `majorMultiplier` is not wrong on its
     * own, it is wrong against the minor beside it, and there is no request that asks
     * about one number. **Which field the answer may light up is the one just left** — see
     * {@link lastBlurred}. Everything else the service said is held until Create.
     *
     * The kind picker passes `$.auto` and `$.fixed` together, because the rule they carry
     * is about the pair.
     */
    fieldBlurred(...paths: string[]): void {
        this.answerFor(...paths);
        this.checkWithService();
    }

    /**
     * What to show under one box, or `''`.
     *
     * Nothing until that box has been left, so a form fills in quietly. Everything after a
     * save attempt, because at that point the reader has said they are finished and a field
     * they never entered is exactly the one they need pointed at.
     */
    errorFor(path: string): string {
        if (!this.showErrors && !this.lastBlurred.has(path)) {
            return '';
        }
        return this.serverErrors.messageFor(path);
    }

    /**
     * Why the threshold line would be refused, one sentence per box, in column order.
     *
     * Listed under the whole line rather than under the box: the columns are too narrow to
     * hold a sentence, and one wrapped under a box would push the boxes beside it out of
     * line. Each is named by its column — see {@link LADDER_FIELDS} — because the sentence is
     * no longer beside the thing it is about, and the outline and the red label are what
     * point at which box.
     *
     * Only the kind on screen. The other set is held so a reader who switches back finds
     * their work, and it is never sent, so a complaint about it would be about a number the
     * service was never told.
     */
    get ladderErrors(): string[] {
        const named = LADDER_FIELDS[this.kind]
            .map(({ path, label }) => {
                const message = this.errorFor(path);
                return isEmpty(message) ? '' : `${label}: ${message}`;
            })
            .filter((message) => !isEmpty(message));
        /* Everything the service refused that no box claims lands here too, rather than in a
         * block at the foot of the page. A refusal a page-length away from the fields it is
         * about reads as being about the page, and the commonest reason for one is a service
         * naming a field this form has not heard of — which is precisely a fault about these
         * boxes, described in a way the form cannot attach. */
        return [...named, ...this.unclaimedErrors];
    }

    /**
     * Whether one box carries a fault, for the outline on it and the colour on its label.
     *
     * The same answer {@link errorFor} gives, asked as a boolean: a box is marked from the
     * moment it has something to say, and unmarked the moment it does not. Two signals for
     * one fault on purpose — the outline finds the box in a row of five that look alike, and
     * the label colour survives the box being scrolled past or read by somebody who cannot
     * pick a red border out of a grey one.
     */
    hasFieldError(path: string): boolean {
        return !isEmpty(this.errorFor(path));
    }

    /** The path of one component id box. The count comes from the alert type. */
    componentPath(index: number): string {
        return `$.componentIds[${index}]`;
    }

    /**
     * What the service refused that no box on this form claims.
     *
     * `$.networkId`, `$` for the request as a whole, and any rule a detector ships after
     * this page was written. Shown in the form's own error block: a refusal nothing
     * displays is a save button that does nothing.
     *
     * **Not held back until somebody saves, unlike a field message.** That gate exists to
     * stop the form reporting that a box has not been filled in yet — and a path with no
     * box on this form cannot be that. Holding one back hides the only evidence that the
     * service is refusing something this page does not understand, which is exactly the
     * case worth seeing early: a service that names a field this form has never heard of
     * is either newer than the form or wrong, and both are worth finding out about before
     * the reader has filled the rest of it in.
     */
    get unclaimedErrors(): string[] {
        return this.serverErrors.unclaimed(this.claimedPaths);
    }

    /**
     * What this form refuses about the scope, when the service has named nothing.
     *
     * **A fallback, and only a fallback.** The message under a box is the service's — see
     * {@link serverErrors} — because one rule worded twice is worded two ways by the second
     * release. But the save is gated on this page's own rules as well, so a press of Create
     * that the gate stops while the service names nothing would be a button that does
     * nothing at all. This is what the form says instead of nothing.
     *
     * Only after a save attempt, like every other message about a box nobody has reached.
     */
    get scopeErrors(): string[] {
        if (!this.showErrors) {
            return [];
        }
        /* A type the service serves no parts for says so in a standing note above the boxes
         * — there are none — so repeating the same sentence here on a press of Create would
         * be the same fault twice on one screen. */
        if (!isEmpty(this.alertType) && this.componentIdParts.length === 0) {
            return [];
        }
        const answered = this.scopePaths.some((path) => !isEmpty(this.serverErrors.messageFor(path)));
        return answered ? [] : this.ownScopeErrors;
    }

    /**
     * Creates the policy, or moves an existing one's thresholds.
     *
     * **The thresholds are the service's to judge.** They go out as typed, and a refusal
     * comes back naming the fields it is about — see {@link handleSaveError}. What stops a
     * save here is the component key alone, because a body without one is a network-wide
     * policy and the service is right to accept it: see {@link errors}.
     */
    save(): void {
        this.showErrors = true;
        this.saveError = '';
        this.checkDenied = false;
        if (this.errors.length > 0) {
            /* Nothing is asked of the service. The only thing this page refuses on its own
             * is an incomplete key, and that is the one body a create may not be asked
             * about either — the answer would be about another policy. See
             * {@link checkWithService}. The sentence is this page's own, under the boxes:
             * {@link scopeErrors}. */
            return;
        }
        if (!this.hasNetwork) {
            this.saveError = 'Select a network before setting a threshold.';
            return;
        }
        this.processing = true;
        this.isLoading = true;
        const saved = this.isEditing
            ? this.alertService.updateLatencyPolicy(this.entityId, this.thresholds)
            : this.alertService.createLatencyPolicy(this.currentNetwork.id, this.request);
        saved
            .then((policy) => {
                this.growlerService.show(
                    new GrowlerData(
                        'success',
                        this.isEditing ? 'Thresholds updated' : 'Latency policy created',
                        /* Says what it replaced rather than only that it saved. A policy
                         * always sits over something — the network policy, or the shipped
                         * defaults — and which one it is now covering is the fact. */
                        policy?.networkDefault
                            ? 'It applies to every component of this alert type.'
                            : 'It overrides the network policy for this component.',
                        ''
                    )
                );
                this.returnToListPage();
            })
            .catch((error) => this.handleSaveError(error))
            .finally(() => {
                this.processing = false;
                this.isLoading = false;
            });
    }

    /** Back to the listing, discarding whatever is on the form. */
    close(): void {
        this.returnToListPage();
    }

    /* Internals -------------------------------------------------------------- */

    /** Every path this form has a box for, including one per component id box. */
    private get claimedPaths(): string[] {
        return [...FIELD_PATHS, ...this.componentIdParts.map((part, index) => this.componentPath(index))];
    }

    /**
     * Asks the service whether the form as it stands would be accepted.
     *
     * **The same request the save will send, with one header on it.** Nothing is created,
     * and the answer is the list of fields that would be refused — see
     * {@link AlertServiceV2.validateLatencyPolicy}. An edit asks about `PATCH`, which
     * carries the thresholds alone, so a complaint about the scope cannot come back for a
     * field nobody can reach.
     *
     * Three things it will not do:
     *
     *  - **Ask twice about the same body.** Tabbing through a row of boxes without typing
     *    would otherwise be one request per box, all with the same answer.
     *  - **Let a slow answer win.** {@link checkSequence} is captured before the request
     *    and compared after it, so an answer about a body that has since changed is dropped
     *    rather than painted.
     *  - **Report its own failure as a fault on the form.** A 403 says the save will be
     *    refused and is worth showing; anything else is a check that could not be made, and
     *    a form that turned red because a request timed out would be inventing faults.
     */
    private checkWithService(): void {
        if (!this.hasNetwork) {
            return;
        }
        if (!this.isEditing && !this.hasWholeKey) {
            /**
             * **A create is never asked about until it carries its whole key.**
             *
             * The id is derived from the network, the alert type and the component, so a
             * body with half a key is a question about a different policy — and a body with
             * no ids at all is a question about the network-wide one. The service answers it
             * honestly and the answer is about something this page is not writing: "a policy
             * already covers this network, alert type and component" is true of the network
             * default and says nothing about the override being filled in.
             *
             * The scope is required on this page — see {@link ownScopeErrors} — so a blank
             * box has an answer already, and it costs no request to give it.
             */
            /* Emptied rather than left standing, for the same reason a cleared row's is: an
             * answer about a key that has since changed is about another policy. Nothing in
             * flight may land on it either. */
            this.serverErrors = new ValidationErrorIndex();
            this.lastCheckedBody = '';
            this.checkSequence++;
            return;
        }
        const body = JSON.stringify(this.isEditing ? this.thresholds : this.request);
        if (body === this.lastCheckedBody) {
            return;
        }
        this.lastCheckedBody = body;
        const sequence = ++this.checkSequence;
        const checked = this.isEditing
            ? this.alertService.validateLatencyPolicyUpdate(this.entityId, this.thresholds)
            : this.alertService.validateLatencyPolicy(this.currentNetwork.id, this.request);
        checked
            .then((errors) => {
                if (sequence !== this.checkSequence) {
                    return;
                }
                this.serverErrors = new ValidationErrorIndex(errors);
                if (this.checkDenied) {
                    this.saveError = '';
                    this.checkDenied = false;
                }
            })
            .catch((error) => {
                /* Cleared so the next blur asks again. A check that failed left the form
                 * knowing nothing, and remembering the body would make that permanent. */
                this.lastCheckedBody = '';
                if (error?.status === 403) {
                    this.saveError = this.isEditing
                        ? 'You do not have permission to change latency policies in this network.'
                        : 'You do not have permission to create latency policies in this network.';
                    this.checkDenied = true;
                    return;
                }
                this.logger.error('The latency policy could not be checked', error);
            });
    }

    /**
     * Reads the whole listing behind each id box.
     *
     * Two jobs, and the second is the one that matters most: searching goes to the
     * controller. It decides whether a box can offer a list at all — {@link canPick}, which is
     * what puts a box on free text and what stops it refusing a typed id. And it fills the
     * panel a box opens before anybody has typed, so the dropdown button answers from what is
     * already cached rather than with a round trip.
     *
     * One request per distinct kind rather than per box: a link policy's two boxes are both
     * edge routers, and the service shares the request in flight anyway, but asking once is
     * clearer than relying on it.
     *
     * Never awaited by anything that matters. A box takes a typed id until a list arrives and
     * starts offering names when it does, so a slow controller delays the convenience and not
     * the form.
     */
    /**
     * Reads the network policy for one alert type, for the placeholders and the millisecond
     * hints under the multipliers.
     *
     * **Never reported, and never blocks anything.** It costs only reference numbers: every
     * box is required, so nothing read here is ever sent, and a reader without `read` on
     * latency policies still gets the platform ladder off the alert type.
     */
    private loadNetworkPolicy(alertType: string): void {
        this.networkPolicy = undefined;
        if (isEmpty(alertType) || !this.hasNetwork) {
            return;
        }
        const requested = alertType;
        this.alertService
            .listLatencyPoliciesPage(this.currentNetwork.id, { alertType: [alertType], networkDefault: true }, 1)
            .then((page) => {
                /* Dropped when the reader has moved on. An answer landing under a different
                 * alert type is the mistake the placeholders exist to avoid. */
                if (this.alertType !== requested) {
                    return;
                }
                this.networkPolicy = (page?.policies || [])[0];
            })
            .catch((error) =>
                this.logger.error(`The network latency policy for ${alertType} could not be read`, error)
            );
    }

    private loadComponentOptions(): void {
        const parts = this.componentIdParts;
        this.componentOptions = parts.map(() => []);
        /* **Each box keeps the panel array it already has.** PrimeNG reads the first
         * `suggestions` reference to change after it asks a question as the answer to that
         * question, and stops listening once it has one — so handing a box a fresh empty
         * array while a search is in flight spends the answer, and the results that arrive
         * afterwards are never shown. Emptying a panel is {@link setAlertType}'s job, where
         * the shape of the form genuinely changed. */
        this.componentSuggestions = parts.map((part, index) => this.componentSuggestions[index] || []);
        this.pendingQueries = parts.map((part, index) => this.pendingQueries[index] || '');
        if (!this.hasNetwork) {
            return;
        }
        const networkId = this.currentNetwork.id;
        for (const kind of new Set(parts)) {
            this.components.listFor(networkId, kind).then((options) => {
                /* Dropped if the reader has changed the alert type while this was in
                 * flight: `parts` is the shape it was read for, and landing it on a
                 * different one would offer routers under a service box. */
                if (this.componentIdParts.join() !== parts.join()) {
                    return;
                }
                this.componentOptions = parts.map((part, index) =>
                    part === kind ? options : this.componentOptions[index] || []
                );
                /* Only into a box that is still empty and has searched for nothing. A panel
                 * that already holds something is either the controller's answer to a search
                 * or this same listing, and replacing it would either undo the search under
                 * the reader or spend the answer to one still in flight. */
                this.componentSuggestions = parts.map((part, index) =>
                    part === kind && isEmpty(this.pendingQueries[index]) && isEmpty(this.componentSuggestions[index])
                        ? options
                        : this.componentSuggestions[index] || []
                );
            });
        }
    }

    /**
     * Reads the policy this page is editing.
     *
     * A 404 is the answer for a policy in a network this caller may not see as well as for
     * one that does not exist — deliberately indistinguishable, so the page says the one
     * thing that is true of both.
     */
    private loadPolicy(policyId: string): void {
        this.isLoading = true;
        this.alertService
            .getLatencyPolicy(policyId)
            .then((policy) => {
                this.policy = policy;
                this.alertType = policy.alertType;
                this.loadNetworkPolicy(policy.alertType);
                this.componentIds = [...this.storedParts];
                /* Read from the field the policy actually carries. A policy the service
                 * would not have stored — neither field set — falls back to auto, whose
                 * fields are then all empty and refused on save. */
                this.kind = policyKind(policy) || 'AUTO';
                this.minorMultiplier = policy.auto?.minorMultiplier;
                this.majorMultiplier = policy.auto?.majorMultiplier;
                this.criticalMultiplier = policy.auto?.criticalMultiplier;
                this.floorMillis = policy.auto?.floorMillis;
                this.ceilingMillis = policy.auto?.ceilingMillis;
                this.minorMillis = policy.fixed?.minorMillis;
                this.majorMillis = policy.fixed?.majorMillis;
                this.criticalMillis = policy.fixed?.criticalMillis;
            })
            .catch((error) => {
                this.logger.error('Failed to read the latency policy', error);
                this.saveError = 'This policy could not be loaded. It may have been deleted.';
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    /**
     * Every reason the form would be refused.
     *
     * The scope is checked on a create only. On an edit it is the policy's key, shown and
     * not editable, and `PATCH` carries the thresholds alone — so a complaint about it
     * would be about a field nobody can reach. The kind is checked either way, because it
     * is part of the thresholds and can be switched on an existing policy.
     */
    /**
     * This page's own scope rules, whatever the service has said.
     *
     * The gate reads this, and {@link scopeErrors} shows it where the service named nothing
     * — one source, so the sentence that stops the save is the sentence the reader gets.
     */
    private get ownScopeErrors(): string[] {
        if (isEmpty((this.alertType || '').trim())) {
            /* The only thing to say. `validateScope` reads an alert type with no parts as
             * "this type is not keyed by a component", which is a different fault and the
             * wrong thing to say to somebody who has not chosen a type yet. */
            return ['An alert type is required.'];
        }
        /* Always on a create, because everything created here is component scoped. A box
         * left blank would either key a policy at half a link or, with all of them blank,
         * quietly create a network policy from a page that does not offer to. */
        return this.isEditing ? [] : validateScope(this.componentIds, this.componentIdParts);
    }

    /**
     * Whether the form carries every component id the alert type is keyed by.
     *
     * What a create may be asked about, and what it may be sent as. A policy written from
     * this page always overrides one component: the thresholds for a whole network are set
     * in the Network Policy section, and a body with no ids is what a network-wide policy
     * looks like on the wire.
     */
    private get hasWholeKey(): boolean {
        const parts = this.componentIdParts;
        return parts.length > 0 && this.request.componentIds.length === parts.length;
    }

    /**
     * Whether a chosen alert type is one no component keys.
     *
     * **The one place the two predicates part.** {@link hasWholeKey} answers what a create
     * may be *asked about*, and it says no here on purpose: a body with no component ids is
     * a question about the network-wide policy, and the service would answer it honestly —
     * naming a policy that already covers the network on `$.alertType`, which is true of the
     * network default and about nothing this page writes. That answer must not reach a box.
     *
     * This answers what may be *typed into*, which is a different question, and the reader
     * is told where it leads: `LatencyPolicyNoParts` sits over the panel and carries the
     * same sentence the save is refused with.
     *
     * **The alert type is tested, not just the parts.** An empty form has no parts either,
     * and reading that as a settled key would open the panel before anything had been
     * chosen — which is the state this gate exists to close.
     */
    private get hasNoComponentKey(): boolean {
        return !isEmpty((this.alertType || '').trim()) && this.componentIdParts.length === 0;
    }

    /**
     * Makes these paths the ones the service's answer may light up, and only these.
     *
     * Replaces rather than adds: see {@link lastBlurred}. Called with one path per blur, and
     * with the pair the kind picker carries.
     */
    private answerFor(...paths: string[]): void {
        this.lastBlurred.clear();
        for (const path of paths) {
            this.lastBlurred.add(path);
        }
    }

    /** Every path the scope section draws a box for, for {@link scopeErrors}. */
    private get scopePaths(): string[] {
        return [
            '$.alertType',
            /* Answered by the service counts as answered however it named the fault, so a
             * complaint about the pair suppresses this page's own sentence the same way a
             * complaint about one box does. Two sentences about one scope, one of them
             * guessed, is the state {@link scopeErrors} exists to avoid. */
            '$.componentIds',
            ...this.componentIdParts.map((part, index) => this.componentPath(index)),
        ];
    }

    /**
     * Turns the service's answer into a line the reader can act on.
     *
     * **409 is the one worth naming.** A policy's id is derived from its network, alert
     * type and component, so a create against a key already covered is refused rather than
     * silently overwriting a threshold somebody else set. "Already exists, edit it" is a
     * next step; "the request failed" is not.
     */
    private handleSaveError(error: any): void {
        this.logger.error('Failed to save the latency policy', error);
        if (error?.status === 400) {
            /* The refusal names its fields as `$.path: message`, which is a sentence rather
             * than a field a box can carry. The check answers the same faults in the shape
             * this form attaches — so it is asked again, and every field lights up because
             * `showErrors` is set by the save that just failed. The body has not changed, so
             * {@link checkWithService} would skip: the memory of it is cleared first. */
            this.lastCheckedBody = '';
            this.checkWithService();
            this.saveError = 'Some of these values were not accepted. The fields below say which.';
            return;
        }
        if (error?.status === 409) {
            this.saveError =
                'A policy already covers this alert type and component. Open it from the listing and change its thresholds instead.';
            return;
        }
        if (error?.status === 403) {
            this.saveError = isNil(this.policy)
                ? 'You do not have permission to create latency policies in this network.'
                : 'You do not have permission to change latency policies in this network.';
            return;
        }
        this.saveError = error?.error?.detail || 'The policy could not be saved.';
    }

    private returnToListPage(): void {
        this.router.navigateByUrl(URLS.LATENCY_POLICIES);
    }
}
